// Client-side social pack generator. Renders 3 crops from the activation hero
// with title + activation number overlay, then bundles into a zip.
import JSZip from "jszip";

interface PackInput {
  title: string;
  subtitle?: string | null;
  scrollCard?: string | null;
  activationNumber?: number | null;
  seriesLabel?: string | null;
  heroImageUrl?: string | null;
  slug: string;
}

interface CropSpec {
  name: string;
  w: number;
  h: number;
  label: string;
}

const CROPS: CropSpec[] = [
  { name: "square-1080", w: 1080, h: 1080, label: "Instagram / Feed" },
  { name: "story-1080x1920", w: 1080, h: 1920, label: "Story / Reels" },
  { name: "landscape-1200x630", w: 1200, h: 630, label: "X / Facebook" },
];

async function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

function drawCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement, w: number, h: number) {
  const targetRatio = w / h;
  const imgRatio = img.width / img.height;
  let sx = 0, sy = 0, sw = img.width, sh = img.height;
  if (imgRatio > targetRatio) {
    sw = img.height * targetRatio;
    sx = (img.width - sw) / 2;
  } else {
    sh = img.width / targetRatio;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
}

function fillFallback(ctx: CanvasRenderingContext2D, w: number, h: number) {
  const grad = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) / 1.2);
  grad.addColorStop(0, "#1a1408");
  grad.addColorStop(1, "#000");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
}

function drawOverlay(ctx: CanvasRenderingContext2D, w: number, h: number, data: PackInput) {
  // Bottom gradient wash for legibility
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, "rgba(0,0,0,0)");
  grad.addColorStop(0.55, "rgba(0,0,0,0.35)");
  grad.addColorStop(1, "rgba(0,0,0,0.85)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const pad = Math.round(w * 0.06);
  const bottom = h - pad;
  ctx.textBaseline = "bottom";
  ctx.textAlign = "left";
  ctx.fillStyle = "#EFE6D2";

  // Kicker
  ctx.font = `600 ${Math.round(w * 0.022)}px "Inter", sans-serif`;
  ctx.fillStyle = "#D9B872";
  const kicker = [
    data.activationNumber ? `№ ${data.activationNumber}` : null,
    data.seriesLabel,
    "KAfrequency",
  ].filter(Boolean).join("  ·  ").toUpperCase();
  ctx.fillText(kicker, pad, bottom - Math.round(w * 0.14));

  // Title
  ctx.fillStyle = "#EFE6D2";
  const titleSize = Math.round(w * 0.055);
  ctx.font = `600 ${titleSize}px "Cormorant Garamond", "Times New Roman", serif`;
  wrapText(ctx, data.title, pad, bottom - Math.round(w * 0.08), w - pad * 2, titleSize * 1.05);

  // Subtitle / scroll card
  const sub = data.scrollCard || data.subtitle;
  if (sub) {
    ctx.fillStyle = "rgba(239,230,210,0.75)";
    const s = Math.round(w * 0.022);
    ctx.font = `italic 400 ${s}px "Cormorant Garamond", serif`;
    wrapText(ctx, `"${sub}"`, pad, bottom - Math.round(w * 0.02), w - pad * 2, s * 1.25, 2);
  }
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, yBottom: number, maxWidth: number, lineHeight: number, maxLines = 4) {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else line = test;
  }
  if (line) lines.push(line);
  const shown = lines.slice(0, maxLines);
  const totalH = shown.length * lineHeight;
  let y = yBottom - totalH + lineHeight;
  for (const l of shown) {
    ctx.fillText(l, x, y);
    y += lineHeight;
  }
}

async function renderCrop(spec: CropSpec, hero: HTMLImageElement | null, data: PackInput): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = spec.w;
  canvas.height = spec.h;
  const ctx = canvas.getContext("2d")!;
  if (hero) drawCover(ctx, hero, spec.w, spec.h);
  else fillFallback(ctx, spec.w, spec.h);
  drawOverlay(ctx, spec.w, spec.h, data);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b!), "image/jpeg", 0.92));
}

export async function generateSocialPack(data: PackInput): Promise<Blob> {
  const hero = data.heroImageUrl ? await loadImage(data.heroImageUrl).catch(() => null) : null;
  const zip = new JSZip();
  for (const spec of CROPS) {
    const blob = await renderCrop(spec, hero, data);
    zip.file(`${data.slug}-${spec.name}.jpg`, blob);
  }
  // Caption text file if provided
  const readme = `KAfrequency — ${data.title}\n\n${data.subtitle ?? ""}\n\nView: https://buddhaka.com/activations/${data.slug}\n`;
  zip.file("README.txt", readme);
  return zip.generateAsync({ type: "blob" });
}

export async function downloadSocialPack(data: PackInput) {
  const blob = await generateSocialPack(data);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `kafrequency-${data.slug}-social-pack.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
